import cx from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { Modal, useModals } from 'almanac-ui/Modal'
import { useSyncWorkspaceMembersCount } from 'screens/LoginOrSignup/hooks/useSyncWorkspaceMembersCount'
import { Workspace } from 'types'
import Account from './Account/Account'
import ArticleStyle from './ArticleStyle/ArticleStyle'
import { TieredBilling } from './TieredBilling/TieredBilling'
import { DirectoryProfile } from './DirectoryProfile/DirectoryProfile'
import { Members } from './Members/Members'
import { Notifications } from './Notifications/Notifications'
import Properties from './Properties/Properties'
import { SecuritySettings } from './Security/SecuritySettings'
import Styles from './SettingsModal.module.scss'
import SettingsSideNav from './SideNav/SettingsSideNav'
import { ALL_ITEMS, SETTINGS_DEFAULT_SCREEN } from './utils'
import { WorkspaceSettings } from './Workspace/WorkspaceSettings'
import { DangerZone } from './DangerZone/DangerZone'
import { REACT_APP_BETA_ENVIRONMENT, REACT_APP_COMMIT_SHA } from 'env'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { AddMember } from 'almanac-ui/icons/16'
import { SettingsPageModal } from './SettingsPageModal'
import { MembershipCard } from './MembershipCard/MembershipCard'
import { useOpenInviteModal } from 'components/Growth/InviteTeamModal/utils'
import { Button } from 'blaze-ui/Button'
import { Gift01 } from 'blaze-ui/icons'
import { ReferralModalV2 } from './ReferralModalV2/ReferralModalV2'
import { useTrialStatus } from 'hooks/useTrialStatus'
import { usePaywall } from 'components/TimeTriggerPaywall/hooks/usePaywall'
import { useAutopilotUpgradeGuard } from 'hooks/useAutopilotUpgradeGuard'

interface Props {
  workspace: Workspace
  onClose: () => void
}

export const VIEW_COMPONENTS: {
  [key: string]: React.ComponentClass<any, any> | ((...args: any) => JSX.Element | null)
} = {
  account: Account,
  profile_directory: DirectoryProfile,
  membership_card: MembershipCard,
  notifications: Notifications,
  danger_zone: DangerZone,
  members: Members,
  billing: TieredBilling,
  general: WorkspaceSettings,
  styles: ArticleStyle,
  security: SecuritySettings,
  properties: Properties,
}

const ReferralButton = () => {
  const { openModal } = useModals()

  return (
    <Button
      variant="secondary"
      size="lg"
      frontIcon={() => <Gift01 size={16} />}
      onPress={() => openModal(ReferralModalV2)}
    >
      Refer & Earn
    </Button>
  )
}

const InviteButton = () => {
  const openInviteModal = useOpenInviteModal({ newStack: true })
  const guardedOpenInviteModal = useAutopilotUpgradeGuard(() => openInviteModal())

  return (
    <Modal.FooterButton variant="secondary" size="lg" frontIcon={AddMember} onPress={guardedOpenInviteModal}>
      Invite members
    </Modal.FooterButton>
  )
}

export const handleClearCacheAndRefresh = async () => {
  if (window.caches) {
    const keys = await window.caches.keys()
    keys.forEach((cacheName) => {
      window.caches.delete(cacheName)
    })
    // eslint-disable-next-line no-restricted-properties
    window.location.reload()
  }
}

export const getHeadReleaseCommentNode = () => {
  const headComments = [...document.head.childNodes].filter(
    (node) => node.nodeType === document.COMMENT_NODE,
  ) as Comment[]
  return headComments.find((node) => /Git \w+, Build [0-9]+/.test(node.data))?.data.trim()
}

const ReleaseInfo = () => {
  const headReleaseCommentNode = useMemo(getHeadReleaseCommentNode, [])

  const commentJobId = headReleaseCommentNode?.match(/Job ([0-9]+)/)?.[1]

  return (
    <div className={Styles.releaseInfo}>
      <div>Environment: {REACT_APP_BETA_ENVIRONMENT ? 'Beta' : 'Main'}</div>
      <div>Release: {REACT_APP_COMMIT_SHA || headReleaseCommentNode}</div>
      {commentJobId ? (
        <a href={`https://app.circleci.com/pipelines/github/almanaclabs/almanac-editor/${commentJobId}`}>
          CI {commentJobId}
        </a>
      ) : null}
      <br />
      <Button onPress={handleClearCacheAndRefresh}>Clear cache and refresh</Button>
    </div>
  )
}

export const SettingsModal = ({ workspace, onClose }: Props) => {
  const history = useHistory()
  const location = useLocation()
  const { isMobile } = useResponsiveDesign()
  const { openModal } = useModals()
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location])
  const settingsView = queryParams.get('settings')
  const show_release = useFlipperFlag('show_release_info')
  const isSettingsModalUpdateEnabled = useFlipperFlag('settings_modal_update')
  const [selectedItem, setSelectedItem] = useState(settingsView || SETTINGS_DEFAULT_SCREEN)
  const { isInTrial, trialDaysLeft } = useTrialStatus()
  const isNewRegistration = useFlipperFlag('new_registration')
  const { openPaywall } = usePaywall()

  useSyncWorkspaceMembersCount(workspace.id)

  useEffect(() => {
    const settingsView = queryParams.get('settings')

    // Check if user can access the setting view based on admin status.
    const viewRestrictedToAdmin = ALL_ITEMS.find((item) => item.key === settingsView)?.admin
    // Redirect to default view if user does not have permission.
    if (settingsView && !workspace.admin && viewRestrictedToAdmin) {
      queryParams.set('settings', SETTINGS_DEFAULT_SCREEN)
      history.push({ search: queryParams.toString() })
      setSelectedItem(SETTINGS_DEFAULT_SCREEN)
    }

    settingsView ? setSelectedItem(settingsView) : setSelectedItem(SETTINGS_DEFAULT_SCREEN)
  }, [queryParams, history, workspace.admin])

  // Open settings page directly if one is passed as a query param that is different from the default page
  useEffect(() => {
    const settingsView = queryParams.get('settings')

    if (isMobile && settingsView && settingsView !== SETTINGS_DEFAULT_SCREEN)
      openModal(
        SettingsPageModal,
        {
          workspace,
          onClose,
          selectedItem: settingsView,
        },
        { newStack: true },
      )
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavItemClick = (key: string) => {
    if ((isNewRegistration && !workspace.paying) || (isInTrial && trialDaysLeft > 0 && key === 'billing')) {
      openPaywall()
      return
    }

    if (isMobile) {
      openModal(
        SettingsPageModal,
        {
          workspace,
          onClose,
          selectedItem: key,
        },
        { newStack: true },
      )
    } else {
      queryParams.set('settings', key)
      history.push({ search: queryParams.toString() })
    }
  }

  // Make sure to clear the query params when the component unmounts
  useEffect(() => {
    return () => {
      onClose()
    }
  }, [onClose])

  const SettingsViewComponent = VIEW_COMPONENTS[selectedItem] || Account

  if (isMobile)
    return (
      <Modal.Root size="md" onPressOutside={onClose} aria-label="Settings">
        <Modal.Header title="Settings" onClose={onClose} />
        <Modal.Content>
          <div className={cx(Styles.mobileSettings, { [Styles.settingsModalUpdate]: isSettingsModalUpdateEnabled })}>
            <SettingsSideNav workspace={workspace} selectedItem={selectedItem} handleClick={handleNavItemClick} />
            {show_release && <ReleaseInfo />}
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="right">
            <InviteButton />
          </Modal.FooterContent>
        </Modal.Footer>
      </Modal.Root>
    )

  return (
    <Modal.Root size="lg" onClose={onClose} onPressOutside={onClose}>
      {!isSettingsModalUpdateEnabled && (
        <Modal.Header
          title="Settings"
          actions={
            <div className={Styles.headerButtons}>
              {!workspace.is_virtual_marketer && <ReferralButton />}
              <InviteButton />
            </div>
          }
        />
      )}
      <div className={Styles.body}>
        <div className={cx(Styles.leftSideBar, { [Styles.settingsModalUpdate]: isSettingsModalUpdateEnabled })}>
          <SettingsSideNav workspace={workspace} selectedItem={selectedItem} handleClick={handleNavItemClick} />
          {show_release && <ReleaseInfo />}
        </div>
        <div
          className={cx(Styles.rightSideBar, { [Styles.settingsModalUpdate]: isSettingsModalUpdateEnabled })}
          data-testid="settingsRightSidebar"
        >
          <SettingsViewComponent workspace={workspace} onClose={onClose} />
        </div>
      </div>
    </Modal.Root>
  )
}
